import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyFormat} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {isScreen} from "./surveyQuestions.util";

/*
    The page rule the editor list, the preview and the guest page share: pages are formed once from the
    author's list, and a guest's visibility is applied to them page by page afterwards. Takes predicates, so
    nothing here reaches into the runner or the public page.
*/

export interface SurveyPage {
    /** The id of the page's first question in author order (its position when that has no id, as in template
     *  previews). Stable while answers change, so the guest's position is stored as it. */
    key: string;
    /** The page's answerable questions in author order, conditional ones included. */
    questions: SurveyQuestion[];
    /** SECTIONS only: the section's heading, taken from its opening question. visiblePages keeps it, so a hidden
     *  opening question does not take the heading with it. */
    sectionTitle?: LocalizedText;
}

export interface SurveyProgress {
    /** One entry per segment, 0–1 filled. */
    segments: number[];
    /** Whole percent for the bar and its screen-reader text: at most 99 until the end screen, 100 there. */
    percent: number;
}

/** Above this many pages, neighbouring pages share a segment, so the bar stays readable on a phone. */
export const MAX_PROGRESS_SEGMENTS = 20;

/** One page at a time: a welcome page, the progress bar, Back / Continue. False for SINGLE_PAGE and undefined. */
export const isPagedFormat = (format?: SurveyFormat): boolean => (
    format === SurveyFormat.MULTI_PAGE || format === SurveyFormat.SECTIONS
);

const pageKey = (question: SurveyQuestion, position: number) => question.id ?? `#${position}`;

/** The answerable questions split into pages: SINGLE_PAGE one, MULTI_PAGE one per question, SECTIONS a new page at
 *  every startsSection. Screens never appear; an undefined format reads as SINGLE_PAGE. */
export const surveyPages = (questions: SurveyQuestion[], format?: SurveyFormat): SurveyPage[] => {
    const answerable = questions.filter(question => !isScreen(question));
    if (!answerable.length) return [];

    if (format === SurveyFormat.MULTI_PAGE) {
        return answerable.map((question, position) => ({key: pageKey(question, position), questions: [question]}));
    }

    if (format !== SurveyFormat.SECTIONS) {
        return [{key: pageKey(answerable[0], 0), questions: answerable}];
    }

    return answerable.reduce<SurveyPage[]>((pages, question, position) => {
        const current = pages[pages.length - 1];

        // The first question opens section 1 whatever it carries; after it, only startsSection opens one.
        if (current && !question.startsSection) {
            current.questions.push(question);
            return pages;
        }

        pages.push({
            key: pageKey(question, position),
            questions: [question],
            ...(question.sectionTitle && {sectionTitle: question.sectionTitle}),
        });
        return pages;
    }, []);
};

/** The pages one guest sees: hidden questions dropped, then the pages left empty. Keys and section titles stay. */
export const visiblePages = (
    pages: SurveyPage[],
    isVisible: (question: SurveyQuestion) => boolean
): SurveyPage[] => pages
    .map(page => ({...page, questions: page.questions.filter(isVisible)}))
    .filter(page => page.questions.length > 0);

/** The question in front of the guest on a page: the one after the furthest answered, capped at the page's last;
 *  the first when nothing on the page is answered. */
export const questionInFront = (
    page: SurveyPage,
    isAnswered: (question: SurveyQuestion) => boolean
): SurveyQuestion | undefined => {
    const furthest = page.questions.reduce((found, question, index) => (isAnswered(question) ? index : found), -1);
    return page.questions[Math.min(furthest + 1, page.questions.length - 1)];
};

/** One segment per page shown, merged evenly above MAX_PROGRESS_SEGMENTS. Pages before the current one are full,
 *  the current one fills by its answered share (answered visible questions / visible questions), later ones are
 *  empty; finished fills them all. */
export const surveyProgress = (
    pageCount: number,
    pageIndex: number,
    answeredShare: number,
    finished: boolean
): SurveyProgress => {
    if (pageCount <= 0) return {segments: [], percent: finished ? 100 : 0};

    const index = Math.min(Math.max(pageIndex, 0), pageCount - 1);
    const share = Math.min(Math.max(answeredShare, 0), 1);
    const fillOf = (page: number) => (finished || page < index ? 1 : page === index ? share : 0);

    const segmentCount = Math.min(pageCount, MAX_PROGRESS_SEGMENTS);
    const segments = Array.from({length: segmentCount}, (_, segment) => {
        const first = Math.floor(segment * pageCount / segmentCount);
        const end = Math.floor((segment + 1) * pageCount / segmentCount);

        let filled = 0;
        for (let page = first; page < end; page++) filled += fillOf(page);
        return filled / (end - first);
    });

    // Only the end screen reads 100: a fully answered last page still has its Submit ahead of it.
    const percent = finished ? 100 : Math.min(Math.round((index + share) / pageCount * 100), 99);
    return {segments, percent};
};
